import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigService } from './config.service';
import { UserInfo, UserPersonInfo } from '../models/identity';
import { ChatMessage } from '../models/chat/chat-message.model';
import { ChatParticipant } from '../models/chat/chat-participant.model';
import { ForwardMessage } from '../models/chat/chat-forward-message.model';
import { distinctUntilChanged } from 'rxjs/operators';
import { parseUtcDate, getShortDateString } from '../helpers/date-helper';
import { TreeViewEntity } from '../models/tree-view-entity.model';

@Injectable()
export class FeedbackService {
    private readonly apiController = 'api/Feedback';
    
    constructor(private readonly httpService: HttpClient, private readonly configService: ConfigService) { }

    public postMessage(chat: ChatParticipant, source: ChatMessage, isAnonymusMessage: boolean) {
        return this.httpService
            .post(`${this.apiController}/sendmessage`,
            this.mapForwardMassage(chat, source, isAnonymusMessage)) as Observable<ForwardMessage>;  
    }

    public getChatsByPattern(namePart: string) {
        return this.httpService.get(`${this.apiController}/chats/search/${namePart}`) as Observable<Array<ChatParticipant>>;
    }

    public getImage(employeeId: number): Observable<Blob> {
        return this.httpService.get(`api/user/picture/${employeeId}`, this.configService.getImageRequestOptions()) as Observable<Blob>;
    }

    public getChats(teammateId?: number) {
        return this.callExtendedByTeammateIdGet(`${this.apiController}/chats`, teammateId) as Observable<Array<ChatParticipant>>;
    }

    public getMessages(chatId: number, teammateId?: number, isRequestedByPm: boolean = false) {
        return this.callExtendedByTeammateIdGet(`${this.apiController}/messages/${chatId}`, teammateId, isRequestedByPm) as Observable<Array<ChatMessage>>;
    }

    public getAnonymousMessages(teammateId?: number) {
        return this.callExtendedByTeammateIdGet(`${this.apiController}/messages/anonymous`, teammateId) as Observable<Array<ChatMessage>>;
    }

    public getChatId(chat: ChatParticipant, teammateId?: number) {
        return this.callExtendedByTeammateIdGet(`${this.apiController}/chatId/${chat.id}`, teammateId) as Observable<number>;
    }

    public getChatReport(dateFrom: Date, dateTo: Date, employeeId: number): Observable<any> {
        let from = getShortDateString(parseUtcDate(dateFrom));
        let to = getShortDateString(parseUtcDate(dateTo));
        let url = `${this.apiController}/report/${from}/${to}` + (employeeId ? '/' + employeeId : '');
        return this.httpService.get<Blob>(url, this.getRequestOptions());
    }

    private requestOptions: Object = {
        headers: new HttpHeaders(
            {
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }),
        responseType: 'blob'
    };
    public getFeedbackChatReport(dateFrom: Date, dateTo: Date, businessUnit: TreeViewEntity,employeeId: number): Observable<any> {
        return this.httpService.post<Blob>(`${this.apiController}/my-bu/report`,
            this.mapFeedbackChatReportFilter(dateFrom, dateTo, businessUnit,employeeId), this.requestOptions);
    }

    public getContactedParticipant(employeeId: number, from?: string, to?: string) {
        return this.httpService.get(`${this.apiController}/admin/employees/incontact/${employeeId}/${from}/${to}`) as Observable<Array<UserInfo>>;
    }

    public getParticipantstMessages(receiverId: number, senderId: number, from?: string, to?: string) {
        return this.httpService.get(
            `${this.apiController}/admin/chat/messages/${receiverId}/${senderId}/${from}/${to}`) as Observable<Array<ChatMessage>>;
    }

    public deleteMessage(messageId: number) {
        return this.httpService.delete(`${this.apiController}/message/delete/${messageId}`).pipe(distinctUntilChanged());
    }

    public getEmployeeAnonymousMessages(employeeId: number, from?: string, to?: string) {
        return this.httpService.get(`${this.apiController}/messages/anonymous/${employeeId}/${from}/${to}`) as Observable<Array<ChatMessage>>;
    }

    private mapForwardMassage(chat: ChatParticipant, source: ChatMessage, isAnonymusMessage: boolean) {
        return {
            id: chat.id,
            feedbackChatId: chat.feedbackChatId,
            message: {
                anonymously: isAnonymusMessage,
                participantId: source.feedbackChatParticipantId,
                text: source.text,
                personId: source.personId,
                isActive: source.isActive
            }
        };
    }

    private mapFeedbackChatReportFilter(startDate: Date, endDate: Date, businessUnit: TreeViewEntity,employeeId: number) {
        let from = getShortDateString(parseUtcDate(startDate));
        let to = getShortDateString(parseUtcDate(endDate));
        return {
            StartDate:from,
            EndDate:to,
            BussinesUnitId:businessUnit && businessUnit.id ? businessUnit.id : null,
            EmployeeId: employeeId
        };
    }

    private getRequestOptions(): Object {
        const options = {
        headers: new HttpHeaders(
            {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }),
        responseType: 'blob'
        };
        return options;
    }

    private callExtendedByTeammateIdGet(url: string, teammateId?: number, isRequestedByPm: boolean = false) {
        return this.httpService.get(this.extendUrlParamsByTeammateId(url, isRequestedByPm, teammateId));
    }

    private extendUrlParamsByTeammateId(sourceUrl: string, isRequestedByPm: boolean, teammateId?: number): string {
        if (!isNaN(teammateId)) {
           return `${sourceUrl}?isRequestedByPm=${isRequestedByPm}&teammateEmployeeId=${teammateId}`;
        }
        return sourceUrl;
    }
}